/* eslint-disable react/no-array-index-key */
import React, {
  ChangeEvent,
  KeyboardEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  FieldValues,
  Path,
  PathValue,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import classNames from 'classnames';

import classes from './VerificationCodeInput.module.scss';
import { VerificationCodeSymbol } from './VerificationCodeSymbol/VerificationCodeSymbol';
import { ResendCode } from './ResendCode/ResendCode';
import { getValidateCode } from './utils/validateCode';
import { addValidateRules } from '../../../utils/reactHookFormUtils';

type VerificationCodeInputProps<
  T extends FieldValues,
  TName extends Path<T>,
> = UseControllerProps<T, TName> & {
  className?: string;
  codeLength?: number;
  nextVerificationCodeCallEpoch?: number | null;
  onFocus?: () => void;
  onResendCodeClick: () => Promise<void>;
};

const VerificationCodeInput = <T extends FieldValues, TName extends Path<T>>({
  control,
  name,
  rules,
  shouldUnregister,
  className,
  codeLength = 6,
  nextVerificationCodeCallEpoch,
  onFocus,
  onResendCodeClick,
}: VerificationCodeInputProps<T, TName>) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const {
    field: { value, onChange, ...field },
    fieldState: { error },
  } = useController({
    control,
    name,
    rules: addValidateRules(rules, { isValid: getValidateCode(codeLength) }),
    shouldUnregister,
    defaultValue: '' as PathValue<T, TName>,
  });

  useEffect(() => {
    inputRef.current?.focus();
  }, [inputRef.current]);

  const symbolsArray = useMemo(() => new Array(codeLength).fill(0), [codeLength]);

  const handleInputFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value.replace(/[^0-9]/g, ''));
  };

  const setCaretToEnd = () => {
    const el = inputRef.current;
    if (!el) return;
    const end = el.value.length;
    requestAnimationFrame(() => {
      el.setSelectionRange(end, end);
    });
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    const { key } = event;

    if (
      key.startsWith('Arrow') ||
      key === 'Home' ||
      key === 'End' ||
      key === 'PageUp' ||
      key === 'PageDown'
    ) {
      event.preventDefault();
      setCaretToEnd();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);

    onFocus?.();
  };

  const handleResendCodeClick = () => {
    onChange('');
    onResendCodeClick();
  };

  return (
    <div className={classNames(classes.container, className)}>
      <div className={classes.row}>
        <p className={classes.title}>Enter Verification Code</p>
        <ResendCode
          nextVerificationCodeCallEpoch={nextVerificationCodeCallEpoch}
          onResendCodeClick={handleResendCodeClick}
        />
      </div>
      <div>
        {/*Hidden input should be in the correct position because devices with virtual keyboard scroll content when the keyboard blocks input. We need to have this working correctly*/}
        <input
          {...field}
          className={classes.hiddenInput}
          ref={(element) => {
            field.ref(element);
            inputRef.current = element;
          }}
          value={value}
          autoComplete="one-time-code"
          maxLength={codeLength}
          inputMode="numeric"
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={() => {
            setIsFocused(false);
          }}
        />
        <div
          className={classes.row}
          onClick={handleInputFocus}
          // prevent input focus loss
          onMouseDown={(e) => e.preventDefault()}
        >
          {symbolsArray.map((_, index) => {
            const isAllFilled = codeLength === value.length;
            const lastIndex = codeLength - 1;
            const nextIndex = value.length;
            const activeSymbolIndex = isAllFilled ? lastIndex : nextIndex;

            const isActive = isFocused && activeSymbolIndex === index;

            return (
              <VerificationCodeSymbol
                key={index}
                value={value?.[index] || ''}
                isActive={isActive}
                error={!!error}
              />
            );
          })}
        </div>
        {error && <p className={classes.error}>{error.message}</p>}
      </div>
    </div>
  );
};

export { VerificationCodeInput };
